import type { ReactNode } from "react";

/** Round icon colours of the booking form's section headers (background / icon). */
export const SECTION_TONE = {
  teal: "bg-[#E0F2EF] text-[#1E6B65]",
  blue: "bg-[#E6F0FB] text-[#1D4E89]",
  violet: "bg-[#EEE9FB] text-[#5B3FA8]",
  orange: "bg-[#FFF1E6] text-[#C2410C]",
  green: "bg-[#DCF5E6] text-[#166534]",
} as const;

/** A section header: a 32px round coloured icon, a 16px bold title and a short
 * grey hint on the right. */
export function SectionHeader({
  icon,
  tone,
  title,
  hint,
}: {
  icon: ReactNode;
  tone: keyof typeof SECTION_TONE;
  title: string;
  hint?: string;
}) {
  return (
    <div className="mb-4 flex flex-wrap items-center gap-x-3 gap-y-1">
      <span
        className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-full ${SECTION_TONE[tone]}`}
      >
        {icon}
      </span>
      <h3 className="text-base font-bold text-ink">{title}</h3>
      {hint && <span className="ml-auto text-xs text-ink-muted">{hint}</span>}
    </div>
  );
}

/** A sub-block inside a card: a thin divider above, then the header. */
export function SubSection({
  children,
  first = false,
  ...header
}: Parameters<typeof SectionHeader>[0] & { children: ReactNode; first?: boolean }) {
  return (
    <section className={first ? "" : "border-t border-[#D4ECE7] pt-5"}>
      <SectionHeader {...header} />
      {children}
    </section>
  );
}
